import { Injectable, OnDestroy } from '@angular/core';
import { inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TokenData, UserData, IdSystemConfig, LoginEvent, LoginOptions, RefreshEvent, LogoutEvent, ErrorEvent, ID_SYSTEM_CONFIG, TokenPayload, RedirectData } from '.';
import { tap, throwError, Subject, catchError, of, Observable, finalize } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class IdSystemService implements OnDestroy {

  private http = new HttpClient(inject(HttpBackend))
  private config = inject<IdSystemConfig>(ID_SYSTEM_CONFIG)
  private deviceId = this.initDevice()
  private eventsSubject = new Subject<LoginEvent | RefreshEvent | LogoutEvent | ErrorEvent>()

  private _accessToken: string | undefined = undefined

  public get accessToken() {
    return this._accessToken
  }

  public get tokenPayload() {
    if (this._accessToken) {
      return jwtDecode<TokenPayload>(this._accessToken)
    }
    throw new Error('No access token')
  }

  public get isAuthed() {
    return this._accessToken !== undefined
  }

  public readonly events$ = this.eventsSubject.asObservable()

  ngOnDestroy(): void {
    this.eventsSubject.complete()
  }

  private get isTelegramWebApp() {
    return typeof window.Telegram !== 'undefined'
      && typeof window.Telegram.WebApp !== 'undefined'
      && window.Telegram.WebApp.initData !== undefined
      && window.Telegram.WebApp.initData.length > 0
  }

  init() {
    let obs: Observable<unknown>
    if (this.isTelegramWebApp) {
      obs = this.initFromTelegramWebApp()
    }
    else {
      obs = this.initSilent()
    }
    return obs.pipe(
      catchError(() => of(undefined))
    )
  }

  private initFromTelegramWebApp() {
    return this.http.post<TokenData>(this.buildUrl('/oauth/v1/telegram/web-app/login'), {
      device_id: this.deviceId,
      init_data: window.Telegram.WebApp.initDataUnsafe,
      from_uri: window.location.origin + window.location.pathname,
    }, {
      withCredentials: true,
    }).pipe(
      tap(data => this.tokenReceived(data)),
      catchError(error => this.errorReceived(error))
    )
  }

  private initSilent() {
    return this.http.get<TokenData>(this.buildUrl('/oauth/v2/silent'), {
      params: {
        device_id: this.deviceId,
      },
      withCredentials: true,
    }).pipe(
      tap(data => this.tokenReceived(data)),
      catchError(error => this.errorReceived(error))
    )
  }

  idpLogin(service: string, idpData: unknown, lang: string, skipLauncher: boolean) {
    const data = {
      'service': service,
      'idp_data': idpData
    }
    return this.http.post<{ redirectUri: string }>(
      `${this.config.apiUrl}/ui/public/idp`,
      data,
      { params: this.queryParams(lang, skipLauncher), withCredentials: true }
    );
  }

  login(options?: LoginOptions) {
    const data: Record<string, string> = {}
    data['redirect_uri'] = options?.redirectUri ?? location.href
    data['from_uri'] = window.location.origin + window.location.pathname
    if (options?.lang) {
      data['lang'] = options.lang
    }
    if (options?.theme) {
      data['theme'] = options.theme
    }
    return this.http.get<RedirectData>(this.buildUrl('/oauth/v2/login'), {
      params: data,
      withCredentials: true,
    }).pipe(
      tap(data => location.href = data.redirectUri),
      catchError(error => this.errorReceived(error))
    )
  }

  logout(): Observable<unknown> {
    return this.http.get(this.buildUrl('/oauth/v2/logout'), {
      withCredentials: true,
    }).pipe(
      catchError(error => this.errorReceived(error)),
      finalize(() => this.onLogout())
    )
  }

  refreshToken() {
    return this.http.get<TokenData>(this.buildUrl('/oauth/v1/refresh'), {
      params: {
        device_id: this.deviceId,
      },
      withCredentials: true,
    }).pipe(
      tap(data => this.tokenRefreshed(data)),
      catchError(error => this.errorReceived(error)),
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status == 401) {
          this.onLogout()
        }
        return throwError(() => error)
      })
    )
  }

  getUser() {
    return this.http.get<UserData>(this.buildUrl('/oauth/v1/userinfo'), {
      withCredentials: true,
    })
  }

  private tokenReceived(data: TokenData) {
    this._accessToken = data.accessToken
    this.eventsSubject.next({event: 'login', accessToken: data.accessToken, accountId: Number(this.tokenPayload.sub)})
  }

  private tokenRefreshed(data: TokenData) {
    this._accessToken = data.accessToken
    this.eventsSubject.next({event: 'refresh', accessToken: data.accessToken})
  }

  private errorReceived(error: any) {
    this.eventsSubject.next({event: 'error', error: error})
    return throwError(() => error)
  }

  private onLogout() {
    this._accessToken = undefined
    this.eventsSubject.next({event: 'logout'})
  }

  private initDevice() {
    let deviceId = localStorage.getItem('id_system_device_id')
    if (!deviceId) {
      deviceId = crypto.randomUUID()
      localStorage.setItem('id_system_device_id', deviceId)
    }
    return deviceId
  }

  private buildUrl(path: string) {
    return `${this.config.apiUrl}/client/${this.config.clientId}/${path.replace(/^\//, '')}`
  }

  private queryParams(lang: string, skipLauncher: boolean) {
    const data: Record<string, string> = {}
    data['redirect_uri'] = location.href
    data['from_uri'] = window.location.origin + window.location.pathname
    data['client_id'] = this.config.clientId
    data['lang'] = lang
    data['skip_launcher'] = skipLauncher ? '1' : ''

    return data
  }
}
